import { useState } from 'react';
import { XIcon } from '../../icons';

interface EmojiPickerProps {
  onSelect: (emoji: string) => void;
  onClose: () => void;
  show: boolean;
}

// Organized emoji categories for better UX
const EMOJI_CATEGORIES = {
  Smileys: [
    '😀',
    '😃',
    '😄',
    '😁',
    '😅',
    '😂',
    '🤣',
    '😊',
    '😇',
    '🙂',
    '🙃',
    '😉',
    '😌',
    '😍',
    '🥰',
    '😘',
    '😗',
    '😙',
    '😚',
    '😋',
    '😛',
    '😝',
    '😜',
    '🤪',
    '🤨',
    '🧐',
    '🤓',
    '😎',
    '🥸',
    '🤩',
    '🥳',
  ],
  Gestures: [
    '👍',
    '👎',
    '👌',
    '✌️',
    '🤞',
    '🤟',
    '🤘',
    '🤙',
    '👈',
    '👉',
    '👆',
    '👇',
    '☝️',
    '👏',
    '🙌',
    '👐',
    '🤲',
    '🤝',
    '🙏',
  ],
  Emotions: [
    '❤️',
    '🧡',
    '💛',
    '💚',
    '💙',
    '💜',
    '🖤',
    '🤍',
    '🤎',
    '💔',
    '❤️‍🔥',
    '❤️‍🩹',
    '💕',
    '💞',
    '💓',
    '💗',
    '💖',
    '💘',
    '💝',
  ],
  Reactions: [
    '😮',
    '😯',
    '😲',
    '😳',
    '🥺',
    '😦',
    '😧',
    '😨',
    '😰',
    '😥',
    '😢',
    '😭',
    '😱',
    '😖',
    '😣',
    '😞',
    '😓',
    '😩',
    '😫',
    '🥱',
  ],
  Celebration: [
    '🎉',
    '🎊',
    '🎈',
    '🎁',
    '🏆',
    '🥇',
    '🥈',
    '🥉',
    '⭐',
    '🌟',
    '✨',
    '💫',
    '🎯',
    '🎪',
    '🎭',
    '🎨',
    '🎬',
    '🎤',
    '🎧',
    '🎵',
  ],
  Objects: [
    '💻',
    '📱',
    '⌨️',
    '🖱️',
    '🖥️',
    '📡',
    '🔋',
    '🔌',
    '💡',
    '🔦',
    '🕯️',
    '📚',
    '📖',
    '📝',
    '✏️',
    '📌',
    '📍',
    '✂️',
    '🔒',
    '🔓',
  ],
  Nature: [
    '🌈',
    '⭐',
    '🌙',
    '☀️',
    '⛅',
    '🌤️',
    '⛈️',
    '🌧️',
    '❄️',
    '☃️',
    '🔥',
    '💧',
    '🌊',
    '🌍',
    '🌎',
    '🌏',
    '🌱',
    '🌿',
    '🍀',
    '🌺',
  ],
};

/**
 * Mobile-friendly emoji picker with categorized emojis
 * - Touch-friendly 44px minimum touch targets
 * - Scrollable categories
 * - Responsive layout
 * - Easy dismiss on mobile
 */
export function EmojiPicker({ onSelect, onClose, show }: EmojiPickerProps) {
  const [activeCategory, setActiveCategory] = useState<keyof typeof EMOJI_CATEGORIES>('Smileys');

  if (!show) return null;

  const categoryKeys = Object.keys(EMOJI_CATEGORIES) as Array<keyof typeof EMOJI_CATEGORIES>;

  return (
    <>
      {/* Mobile overlay - tap outside to close */}
      <div
        className="fixed inset-0 bg-black/50 z-40 md:hidden"
        onClick={onClose}
        data-testid="emoji-picker-overlay"
      />

      {/* Emoji picker panel */}
      <div className="fixed bottom-0 left-0 right-0 md:absolute md:bottom-auto md:left-auto md:right-auto md:bottom-full md:mb-2 bg-slate-800 border border-slate-700 rounded-t-xl md:rounded-xl shadow-xl z-50 max-h-[60vh] md:max-h-96 w-full md:w-80">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-slate-700">
          <h3 className="text-sm font-semibold text-white">Pick an emoji</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-700 rounded-lg transition-colors touch-manipulation"
            aria-label="Close emoji picker"
            data-testid="emoji-picker-close"
          >
            <XIcon className="text-slate-400" />
          </button>
        </div>

        {/* Category tabs - horizontal scroll on mobile */}
        <div className="border-b border-slate-700 overflow-x-auto">
          <div className="flex px-2 py-2 gap-1 min-w-max">
            {categoryKeys.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors touch-manipulation ${
                  activeCategory === category
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
                data-testid={`emoji-category-${category}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Emoji grid - scrollable */}
        <div className="p-3 overflow-y-auto max-h-64 md:max-h-72">
          <div className="grid grid-cols-6 sm:grid-cols-7 md:grid-cols-8 gap-1">
            {EMOJI_CATEGORIES[activeCategory].map((emoji) => (
              <button
                key={emoji}
                onClick={() => {
                  onSelect(emoji);
                  onClose();
                }}
                className="min-w-[44px] min-h-[44px] flex items-center justify-center text-2xl hover:bg-slate-700 rounded-lg transition-colors active:scale-95 touch-manipulation"
                title={`Insert ${emoji}`}
                data-testid={`emoji-${emoji}`}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>

        {/* Footer hint - desktop only */}
        <div className="hidden md:block px-3 py-2 border-t border-slate-700 bg-slate-900/50">
          <p className="text-xs text-slate-400">
            Tip: Type <code className="px-1 bg-slate-700 rounded">:emoji_name</code> to autocomplete
          </p>
        </div>
      </div>
    </>
  );
}
